import { useSignal } from "@preact/signals";

function filesFromList(list: FileList | null): readonly File[] {
  return list === null ? [] : Array.from(list);
}

function hasFiles(event: DragEvent): boolean {
  return event.dataTransfer?.types.includes("Files") ?? false;
}

interface FileDropHandlers {
  readonly dragActive: boolean;
  readonly onDragEnter: (event: DragEvent) => void;
  readonly onDragOver: (event: DragEvent) => void;
  readonly onDragLeave: (event: DragEvent) => void;
  readonly onDrop: (event: DragEvent) => void;
}

export function useFileDrop(
  onFiles: (files: readonly File[]) => void,
): FileDropHandlers {
  const dragDepth = useSignal(0);

  const onDragEnter = (event: DragEvent): void => {
    if (!hasFiles(event)) {
      return;
    }
    event.preventDefault();
    dragDepth.value++;
  };

  const onDragOver = (event: DragEvent): void => {
    if (!hasFiles(event)) {
      return;
    }
    event.preventDefault();
    if (event.dataTransfer !== null) {
      event.dataTransfer.dropEffect = "copy";
    }
  };

  const onDragLeave = (event: DragEvent): void => {
    if (!hasFiles(event)) {
      return;
    }
    event.preventDefault();
    dragDepth.value = Math.max(0, dragDepth.value - 1);
  };

  const onDrop = (event: DragEvent): void => {
    event.preventDefault();
    dragDepth.value = 0;
    onFiles(filesFromList(event.dataTransfer?.files ?? null));
  };

  return {
    dragActive: dragDepth.value > 0,
    onDragEnter,
    onDragOver,
    onDragLeave,
    onDrop,
  };
}
